import { useEffect } from 'react';
import { useRouter } from '@/context/RouterContext';
import { TelemetryPage } from '@/types/routes';
import { Header } from '@/components/navigation/Header';
import { useAuth } from '@/context/AuthContext';
import { LoginPage } from '@/components/auth/LoginPage';
import { OAuthConsentPage } from '@/components/auth/OAuthConsentPage';
import { SettingsPage } from '@/components/settings/SettingsPage';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { UploadPage } from '@/components/upload/UploadPage';
import { Footer } from '@/components/navigation/Footer';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { ErrorBoundary } from '@shared/ErrorBoundary';
import { OfflineGuard } from '@/components/navigation/OfflineGuard';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { Dashboard } from '@/components/dashboard/Dashboard';
import { SessionDetail } from '@/components/session/SessionDetail';
import { LapAnalysis } from '@/components/lap/LapAnalysis';
import { CornerAnalysis } from '@/components/corner/CornerAnalysis';
import { BrakingAnalysis } from '@/components/braking/BrakingAnalysis';
import { SectorAnalysis } from '@/components/sector/SectorAnalysis';
import { AggregationAnalysis } from '@/components/aggregation/AggregationAnalysis';
import { AggregationDiffAnalysis } from '@/components/aggregationdiff/AggregationDiffAnalysis';
import { TyreAnalysis } from '@/components/tyre/TyreAnalysis';
import { TyreOverview } from '@/components/tyre/TyreOverview';
import { RacesOverview } from '@/components/races/RacesOverview';
import { SeasonDetail } from '@/components/races/SeasonDetail';
import { EventOverview } from '@/components/races/EventOverview';
import { EventDetail } from '@/components/races/EventDetail';

import { DriversList } from '@/components/races/DriversList';

import { DriverHistory } from '@/components/races/DriverHistory';
import { DriverPace } from '@/components/races/DriverPace';
import { DriverStats } from '@/components/races/DriverStats';

import { TracksOverview } from '@/components/tracks/TracksOverview';
import { TrackDetail } from '@/components/tracks/TrackDetail';
import { MaterialsPage } from '@/components/materials/MaterialsPage';
import { TeamsPage } from '@/components/teams/TeamsPage';
import { TeamDetailPage } from '@/components/teams/TeamDetailPage';
import { SetupPage } from '@/components/setup/SetupPage';
import { SetupCoachPage } from '@/components/coach/SetupCoachPage';
import { AnalysisHistoryPage } from '@/components/analysis/AnalysisHistoryPage';
import { PrivacyPolicyPage } from '@/components/privacy/PrivacyPolicyPage';
import { TermsPage } from '@/components/terms/TermsPage';
import { TermsAcceptanceGate } from '@shared/auth/TermsAcceptanceGate';
import { Markdown } from '@com.yanicksenn/shared';
import termsEnMarkdown from '@resources/terms_and_conditions.en.md';
import termsDeMarkdown from '@resources/terms_and_conditions.de.md';
import termsFrMarkdown from '@resources/terms_and_conditions.fr.md';
import termsEsMarkdown from '@resources/terms_and_conditions.es.md';
import termsItMarkdown from '@resources/terms_and_conditions.it.md';
import termsNlMarkdown from '@resources/terms_and_conditions.nl.md';

function App() {
  const { view, params } = useRouter();
  const { user, isLoading } = useAuth();
  const { showToast } = useToast();
  const { t } = useI18n();
  const { isOnline } = useNetworkStatus();

  // Listen for auto-reported bug events from the RPC client
  useEffect(() => {
    const handler = () => showToast(t('error.autoReported'), 'error');
    window.addEventListener('bug:auto-reported', handler);
    return () => window.removeEventListener('bug:auto-reported', handler);
  }, [showToast, t]);

  const renderView = () => {
    switch (view) {
      case TelemetryPage.Dashboard:
        return <Dashboard />;
      case TelemetryPage.Session:
        return <SessionDetail sessionId={params.sessionId} initialView={params.view} initialTab={params.tab} />;
      case TelemetryPage.Lap:
        return <LapAnalysis sessionId={params.sessionId} lapNumber={parseInt(params.lapNumber, 10)} tab={params.tab} />;
      case TelemetryPage.CornerAnalysis:
        return <CornerAnalysis trackName={decodeURIComponent(params.trackName || '')} />;
      case TelemetryPage.BrakingAnalysis:
        return <BrakingAnalysis trackName={decodeURIComponent(params.trackName || '')} />;
      case TelemetryPage.SectorAnalysis:
        return <SectorAnalysis trackName={decodeURIComponent(params.trackName || '')} initialSessionId={params.session} />;
      case TelemetryPage.LapDiffAnalysis:
        return <AggregationDiffAnalysis trackName={decodeURIComponent(params.trackName || '')} initialSessionId={params.session} initialMode="lap" />;
      case TelemetryPage.AggregationAnalysis:
        return <AggregationAnalysis trackName={decodeURIComponent(params.trackName || '')} initialSessionId={params.session} />;
      case TelemetryPage.AggregationDiffAnalysis:
        return <AggregationDiffAnalysis trackName={decodeURIComponent(params.trackName || '')} initialSessionId={params.session} />;
      case TelemetryPage.TyreAnalysis:
        return <TyreAnalysis tyreId={decodeURIComponent(params.tyreId || '')} />;
      case TelemetryPage.TyreOverview:
        return <TyreOverview />;
      case TelemetryPage.Races:
        return <RacesOverview />;
      case TelemetryPage.RaceSeason:
        return <SeasonDetail championshipId={params.championshipId} />;
      case TelemetryPage.RaceEvent:
        return <EventOverview championshipId={params.championshipId} eventId={params.eventId} />;
      case TelemetryPage.RaceEventCategory:
        return <EventDetail championshipId={params.championshipId} eventId={params.eventId} initialCategory={decodeURIComponent(params.categoryId || '')} initialType={params.type} initialDetailTab={params.detail} highlightDriverId={params.driver} />;

      case TelemetryPage.RaceDrivers:
        return <DriversList />;
      case TelemetryPage.RaceDriver:
        return <DriverHistory driverId={params.driverId} />;
      case TelemetryPage.RaceDriverPace:
        return <DriverPace driverId={params.driverId} />;
      case TelemetryPage.RaceDriverStats:
        return <DriverStats driverId={params.driverId} />;

      case TelemetryPage.Tracks:
        return <TracksOverview />;
      case TelemetryPage.TrackDetail:
        return <TrackDetail source={params.source} trackId={decodeURIComponent(params.trackId)} initialTab={params.tab as 'layout' | 'measured-laps' | 'official-laps' | 'races' | undefined} />;
      case TelemetryPage.Inventory:
        return <MaterialsPage />;
      case TelemetryPage.Teams:
        return <TeamsPage />;
      case TelemetryPage.TeamDetail:
        return <TeamDetailPage teamId={params.teamId} />;
      case TelemetryPage.Setup:
        return <SetupPage />;
      case TelemetryPage.SetupCoach:
        return <SetupCoachPage />;
      case TelemetryPage.AnalysisHistory:
        return <AnalysisHistoryPage />;
      case TelemetryPage.Upload:
        return <UploadPage />;
      case TelemetryPage.Settings:
        return <SettingsPage />;
      case TelemetryPage.Login:
        return <LoginPage />;
      case TelemetryPage.OAuthAuthorize:
        return <OAuthConsentPage params={params} />;
      case TelemetryPage.Privacy:
        return <PrivacyPolicyPage />;
      case TelemetryPage.Terms:
        return <TermsPage />;
      default:
        return <Dashboard />;
    }
  };

  const hideHeader = view === TelemetryPage.CornerAnalysis || view === TelemetryPage.BrakingAnalysis || view === TelemetryPage.SectorAnalysis || view === TelemetryPage.TyreAnalysis || view === TelemetryPage.LapDiffAnalysis || view === TelemetryPage.AggregationAnalysis || view === TelemetryPage.AggregationDiffAnalysis || view === TelemetryPage.TyreOverview || view === TelemetryPage.SetupCoach;

  // Public standalone legal pages (accessible with or without authentication)
  if (view === TelemetryPage.Privacy) {
    return <PrivacyPolicyPage />;
  }
  if (view === TelemetryPage.Terms) {
    return <TermsPage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Not authenticated → force login page (unless on OAuth consent or Login page)
  if (!user && view !== TelemetryPage.Login && view !== TelemetryPage.OAuthAuthorize) {
    window.location.hash = '#/login';
    return null;
  }

  // Authenticated but on login page → redirect to home
  if (user && view === TelemetryPage.Login) {
    window.location.hash = '#/';
    return null;
  }

  // Authenticated user who has not accepted the latest Terms & Conditions
  if (user && user.termsAccepted === false) {
    return (
      <div className="min-h-screen flex flex-col bg-bg-base [&_.login-page]:!min-h-0 [&_.login-page]:flex-1">
        <TermsAcceptanceGate
          fallbackMarkdown={{
            en: termsEnMarkdown,
            de: termsDeMarkdown,
            fr: termsFrMarkdown,
            es: termsEsMarkdown,
            it: termsItMarkdown,
            nl: termsNlMarkdown,
          }}
          renderMarkdown={(md) => <Markdown content={md} />}
        />
        <Footer />
      </div>
    );
  }

  // OAuth consent page (renders standalone without header, with footer)
  if (view === TelemetryPage.OAuthAuthorize) {
    return (
      <div className="min-h-screen flex flex-col bg-bg-base">
        <OAuthConsentPage params={params} />
        <Footer />
      </div>
    );
  }

  // Login page
  if (view === TelemetryPage.Login) {
    return (
      <div className="min-h-screen flex flex-col bg-bg-base [&_.login-page]:!min-h-0 [&_.login-page]:flex-1">
        <LoginPage />
        <Footer />
      </div>
    );
  }

  return (
    <main className={`min-h-screen flex flex-col ${hideHeader ? '' : 'pt-[60px] sm:pt-[52px]'}`}>
      {!hideHeader && <Header />}

      <div className="flex-1 w-full max-w-[960px] mx-auto px-5 sm:px-8 pt-8 pb-10">
        <ErrorBoundary
          title={t('errorBoundary.title')}
          message={t('errorBoundary.message')}
          reloadLabel={t('errorBoundary.reload')}
          resetLabel={t('errorBoundary.reset')}
          showDetailsLabel={t('errorBoundary.showDetails')}
          hideDetailsLabel={t('errorBoundary.hideDetails')}
        >
          <OfflineGuard>
            <div key={view} className="animate-route-transition">
              {renderView()}
            </div>
          </OfflineGuard>
        </ErrorBoundary>
      </div>

      <Footer />
    </main>
  );
}

export default App;
